import { updateAdditionalUserInfo } from "app/utils/users";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signUp, signIn } from "aws/auth";
import { useAuth } from "aws/AuthContext";
import { DASHBOARD_ROUTE } from "app/constants/route";
import toast from "react-hot-toast";

// Import individual Lambda functions to avoid TypeScript issues
import {
  registerUser,
  verifyUser,
  VerifyRegistrationCodeOutput
} from "aws/lambda";

// Create a direct import for verifyRegistrationCode to work around TypeScript issues
// This uses the same implementation but assigns it to a local function
const verifyRegistrationCode = async (code: string): Promise<VerifyRegistrationCodeOutput> => {
  try {
    // Reuse the same implementation logic as the Lambda service
    const functionPath = "registration/verify";
    
    // Make a direct fetch to the API with the registration code
    const endpoint = `${process.env.REACT_APP_API_GATEWAY_URL}${functionPath}`;
    const options: RequestInit = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ registrationCode: code }),
    };
    
    const response = await fetch(endpoint, options);
    
    if (!response.ok) {
      let errorMessage = "";
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || JSON.stringify(errorData);
      } catch (e) {
        errorMessage = response.statusText;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error verifying registration code:", error);
    
    if (error instanceof Error && error.message.includes("CORS")) {
      return {
        valid: false,
        error: "Unable to verify registration code due to network restrictions. Please try again later.",
      };
    }
    
    return { 
      valid: false, 
      error: error instanceof Error ? error.message : String(error)
    };
  }
};

type Props = {};

const initialValues = {
  formValue: {
    username: "",
    email: "",
    password: "",
  },
};

type SignUpSteps = 1 | 2; // 1: School code, 2: User details

const useSignUpForm = (props: Props) => {
  // Current step in the form process (1: School code, 2: User details)
  const [currentStep, setCurrentStep] = React.useState<SignUpSteps>(1);
  const { refreshUser } = useAuth(); // Get the refreshUser function from AuthContext

  const navigate = useNavigate();
  let { search } = useLocation();
  const token = new URLSearchParams(search).get("token");

  const [isLoading, setIsLoading] = React.useState(false);
  const [formValue, setFormValue] = React.useState(initialValues?.formValue);
  const [errMessage, setErrMessage] = React.useState("");
  const [isPasswordValid, setIsPasswordValid] = React.useState(false);
  
  // Registration code state
  const [registrationCode, setRegistrationCode] = React.useState("");
  const [codeVerification, setCodeVerification] = React.useState({
    loading: false,
    status: "notVerified" as "notVerified" | "success" | "failed",
    error: "",
    email: "",
  });

  const clearError = () => {
    setErrMessage("");
  };

  const updateFormValue = (key: string, value: any) => {
    try {
      const newFormValue = { ...formValue, [key]: value };
      setFormValue(newFormValue);
      // Clear error when user modifies input
      clearError();
    } catch (err) {
      console.error("Error updating form value:", err);
    }
  };

  // Update registration code input
  const updateRegistrationCode = (value: string) => {
    setRegistrationCode(value);
  };

  // Verify registration code
  const verifyUserRegistrationCode = async () => {
    if (!registrationCode || registrationCode.length !== 6) {
      setCodeVerification({
        ...codeVerification,
        status: "failed",
        error: "Registration code must be 6 characters",
      });
      return;
    }

    try {
      setCodeVerification({
        ...codeVerification,
        loading: true,
        status: "notVerified",
        error: "",
      });

      const result = await verifyRegistrationCode(registrationCode);

      if (result.valid) {
        // Update form with email if provided
        if (result.email) {
          updateFormValue("email", result.email);
        }
        
        setCodeVerification({
          loading: false,
          status: "success",
          error: "",
          email: result.email || "",
        });

        // Move to the next step
        setCurrentStep(2);
      } else {
        setCodeVerification({
          loading: false,
          status: "failed",
          error: "Invalid registration code",
          email: "",
        });
      }
    } catch (error) {
      setCodeVerification({
        loading: false,
        status: "failed",
        error: "Error verifying registration code",
        email: "",
      });
    }
  };

  // Handle initial form submission (Step 2)
  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    clearError();

    try {
      // Sign up with Cognito
      console.log("Signing up with Cognito...");
      await signUp({
        username: formValue.email,
        password: formValue.password,
        email: formValue.email,
      });

      console.log("Cognito sign up successful");

      // Verify user before sign in
      try {
        await verifyUser(formValue.email);
      } catch (verifyError) {
        console.error("Error verifying user:", verifyError);
        throw new Error("Failed to verify user. Please try again.");
      }

      // Sign in after successful verification
      await signIn({
        username: formValue.email,
        password: formValue.password,
      });

      // Force a refresh of the auth state
      await refreshUser();

      // Register user with the verified Cognito user ID
      try {
        // Get the Cognito user ID from the current session
        const { getCurrentUser } = await import("aws/auth");
        const currentUser = await getCurrentUser();

        if (currentUser && currentUser.userId) {
          // Call register user lambda with cognitoUserId (schoolCode kept for backward compatibility)
          await registerUser({
            schoolCode: registrationCode, // Still keeping for backward compatibility
            cognitoUserId: currentUser.userId,
          });

          console.log("User successfully registered");
        }
      } catch (registerError) {
        console.error("Error registering user with backend:", registerError);
        
        // Handle specific error cases from the new Lambda implementation
        if (registerError instanceof Error && 
            registerError.message.includes("No user found with your email address")) {
          setErrMessage(
            "No user found with your email address. Please contact an administrator to create your account."
          );
          setIsLoading(false);
          return; // Stop the process, don't proceed to dashboard
        }
        
        // For other errors, we'll continue with the flow but log the error
        // This maintains backward compatibility with the previous behavior
      }

      // Update additional user info for local storage
      updateAdditionalUserInfo(formValue);

      // Clear modal flags to ensure new users see the tutorial modal
      localStorage.removeItem('academix-enrollment-tutorial-shown');
      localStorage.removeItem('seen_notifications')

      // Show success message and redirect
      toast.success("Registration successful! Redirecting to dashboard...")
      setTimeout(() => {
        navigate(DASHBOARD_ROUTE);
      }, 3000);
    } catch (error) {
      console.error("Registration error:", error);

      // This is an error with the registration process
      setErrMessage(
        error instanceof Error
          ? error.message
          : "Registration failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Extract registration code from URL parameter if available
  React.useEffect(() => {
    if (token) {
      setRegistrationCode(token);
      verifyRegistrationCode(token)
        .then((result) => {
          console.log("Registration code verification result:", result);
          if (result.valid) {
            // Update form with email if provided
            if (result.email) {
              updateFormValue("email", result.email);
            }
            
            setCodeVerification({
              loading: false,
              status: "success",
              error: "",
              email: result.email || "",
            });
            // Move to step 2 if code is valid
            setCurrentStep(2);
          } else {
            setCodeVerification({
              loading: false,
              status: "failed",
              error: "Invalid registration code",
              email: "",
            });
          }
        })
        .catch((error) => {
          console.error("Error verifying registration code from URL:", error);
          setCodeVerification({
            loading: false,
            status: "failed",
            error: "Error verifying registration code",
            email: "",
          });
        });
    }
  }, [token]);

  return {
    isPasswordValid,
    setIsPasswordValid,
    currentStep,
    setCurrentStep,
    formValue,
    updateFormValue,
    isLoading,
    setIsLoading,
    onSubmit,
    errMessage,
    setErrMessage,
    clearError,
    // Registration code related
    registrationCode,
    updateRegistrationCode,
    verifyUserRegistrationCode,
    codeVerification,
  };
};

export default useSignUpForm;
